/**
 * Date utility functions with robust error handling
 */

/**
 * Safely formats a date value to Brazilian locale format (dd/mm/yyyy)
 * @param dateValue - Date string, Date object, or any value that might be a date
 * @param options - Optional formatting options
 * @returns Formatted date string or fallback value
 */
export const formatDate = (
  dateValue: any, 
  options: {
    fallback?: string;
    includeTime?: boolean;
    format?: 'short' | 'long';
  } = {}
): string => {
  const { 
    fallback = 'Data inválida', 
    includeTime = false, 
    format = 'short' 
  } = options;

  // Handle null, undefined, or empty values
  if (!dateValue) {
    return fallback;
  }

  let date: Date;
  const isMidnightUTCString = (val: any) => {
    if (typeof val !== 'string') return false;
    // Detect ISO strings persisted at midnight UTC
    return /T00:00:00(?:\.000)?(?:Z|\+00:00)$/.test(val);
  };

  try {
    // If it's already a Date object
    if (dateValue instanceof Date) {
      date = dateValue;
    }
    // If it's a string or number, try to parse it
    else {
      date = new Date(dateValue);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return fallback;
    }

    // Format based on requirements
    const isMidnightUTC = isMidnightUTCString(dateValue);
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: format === 'long' ? 'long' : '2-digit',
      year: 'numeric',
      // If original value is midnight UTC, format using UTC to avoid day rollback in BR tz
      timeZone: isMidnightUTC ? 'UTC' : 'America/Sao_Paulo'
    };

    if (includeTime) {
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
    }

    return date.toLocaleDateString('pt-BR', formatOptions);
  } catch (error) {
    console.error('Date formatting error:', error, 'for value:', dateValue);
    return fallback;
  }
};

/**
 * Formats date for display in task cards (compact format)
 * @param dateValue - Date to format
 * @returns Formatted date string (dd/mm/yyyy) or fallback
 */
export const formatTaskDate = (dateValue: any): string => {
  return formatDate(dateValue, {
    fallback: '--/--/----',
    format: 'short'
  });
};

/**
 * Formats date with time for detailed views
 * @param dateValue - Date to format
 * @returns Formatted date string with time or fallback
 */
export const formatDateTime = (dateValue: any): string => {
  return formatDate(dateValue, {
    fallback: 'Data não disponível',
    includeTime: true,
    format: 'short'
  });
};

/**
 * Formats date in a readable format for headers and descriptions
 * @param dateValue - Date to format
 * @returns Formatted date string (dd de mês de yyyy) or fallback
 */
export const formatReadableDate = (dateValue: any): string => {
  return formatDate(dateValue, {
    fallback: 'Data não informada',
    format: 'long'
  });
};

/**
 * Checks if a date value is valid
 * @param dateValue - Value to check
 * @returns True if valid date, false otherwise
 */
export const isValidDate = (dateValue: any): boolean => {
  if (!dateValue) return false;
  
  try {
    const date = new Date(dateValue);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};

/**
 * Converts a date to ISO string safely
 * @param dateValue - Date to convert
 * @returns ISO string or null if invalid
 */
export const toISOString = (dateValue: any): string | null => {
  if (!isValidDate(dateValue)) {
    return null;
  }
  
  try {
    return new Date(dateValue).toISOString();
  } catch {
    return null;
  }
};
